import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { matchPath } from 'react-router';
import { useHistory } from 'react-router-dom';

import { Link } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import SvgIcon from '@material-ui/core/SvgIcon';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core/styles';
import LeftArrowIcon from '@material-ui/icons/ArrowBackIosRounded';
import RightArrowIcon from '@material-ui/icons/ArrowForwardIosRounded';
import Skeleton from '@material-ui/lab/Skeleton';
import classnames from 'classnames';
import {
  PATHWAYS_START_URL,
  TEACHERS_HOME_URL,
  TEACHER_ASSIGNMENTS_V2_URL,
  TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
} from 'config/urls';
import logoSmall from 'images/logo-small.svg';
import logoBig from 'images/logo.svg';
import { isNil, isNull, map } from 'lodash';
import { useUserLatestAssignemntsPerSection } from 'sdk';

import TeacherAssignmentCreate from 'pages/Teachers/Assignments/Create';
import { ReactComponent as AssignemntsIcon } from 'pages/Teachers/shared/TeacherPageLayout/components/SideNav/assets/assignment-icon.svg';
import SideNavItemWrapper from 'pages/Teachers/shared/TeacherPageLayout/components/SideNavItemWrapper';
import { colors } from 'theme/palette';
import { formatRelativeTime } from 'utils/date';
import { useDialog } from 'utils/hooks';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';
import { useIsMobile } from 'utils/resizing';
import { reverse } from 'utils/urls';

import AssignmentDialogContext from 'components/AssignmentDialogContext';
import Dialog from 'components/Dialog';
import FreeTrialReminder from 'components/FreeTrialReminder';
import Typography from 'components/Typography';
import UserContext from 'components/UserContext';

import styles from './styles.module.scss';
import { getLeftNavigationItems } from './utils';

const HOME_PAGE_ENABLED = process.env.REACT_APP_HOME_PAGE_ENABLED === 'true';
const SIDE_NAVIGATION_ASSIGNMENTS_LIST_ENABLED =
  process.env.REACT_APP_SIDE_NAVIGATION_ASSIGNMENTS_LIST_ENABLED === 'true';

const useStylesBootstrap = makeStyles((theme) => ({
  arrowCelebrate: {
    color: '#9CD29B'
  },
  arrowSupport: {
    color: '#ECD19C'
  },
  arrowIntervene: {
    color: '#E6798C'
  },
  arrowNeedsReview: {
    color: '#454A7C'
  },
  arrowLoading: {
    color: '#C8C7FF;'
  },
  tooltipCelebrate: {
    backgroundColor: '#9CD29B'
  },
  tooltipSupport: {
    backgroundColor: '#ECD19C'
  },
  tooltipIntervene: {
    backgroundColor: '#E6798C'
  },
  tooltipNeedsReview: {
    backgroundColor: '#454A7C'
  },
  tooltipLoading: {
    backgroundColor: '#C8C7FF;'
  }
}));

const SideNav = ({ open, toggle, lgScreen }) => {
  const isMobile = useIsMobile();
  const { user } = useContext(UserContext);
  const classes = useStylesBootstrap();
  const [, setTick] = useState(0);

  const [results, setResults] = useState([]);
  const [shouldExpandAssignmentsList, setShouldExpandAssignmentsList] =
    useState(false);

  const colorClassMap = {
    celebrate: classes.tooltipCelebrate,
    support: classes.tooltipSupport,
    intervene: classes.tooltipIntervene,
    needsReview: classes.tooltipNeedsReview,
    loading: classes.tooltipLoading
  };

  const arrowColorClassMap = {
    celebrate: classes.arrowCelebrate,
    support: classes.arrowSupport,
    intervene: classes.arrowIntervene,
    needsReview: classes.arrowNeedsReview,
    loading: classes.arrowLoading
  };

  const logoRedirectUrl =
    isMobile && user.is_pathways_user
      ? PATHWAYS_START_URL
      : HOME_PAGE_ENABLED
      ? TEACHERS_HOME_URL
      : TEACHER_ASSIGNMENTS_V2_URL;

  const {
    isOpened: isNoAssignmentsDialogOpened,
    closeDialog: closeNoAssignmentsDialog
  } = useDialog();

  const [pagination, setPagination] = useState({
    offset: 0,
    limit: 20
  });

  const filters = useMemo(
    () => ({
      offset: pagination.offset
    }),
    [pagination]
  );

  const {
    data: latestAssignmentsPerSection,
    isLoading: isLoadingLatestAssignments
  } = useUserLatestAssignemntsPerSection({ params: filters });

  const history = useHistory();

  const [lastUpdated, setLastUpdated] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setPagination({
        offset: 0,
        limit: 20
      });
      setLastUpdated(new Date());
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const { isAssignmentDialogOpened, setIsAssignmentDialogOpened } = useContext(
    AssignmentDialogContext
  );

  const toggleNavigationOnTabletResolution = () => {
    if (!lgScreen) {
      toggle();
    }
  };

  const leftNavigationItems = useMemo(() => {
    if (user) {
      return getLeftNavigationItems({ user, isMobile });
    }
    return [];
  }, [user, isMobile]);

  const isSelected = ({ url, currentPath }) => {
    if (url.startsWith('/pathways/insights')) {
      return matchPath(currentPath, {
        path: '/pathways/insights',
        exact: false
      });
    }

    if (url.startsWith('/pathways')) {
      return (
        currentPath.startsWith('/pathways') &&
        !currentPath.includes('/insights')
      );
    }

    if (url.startsWith('/teacher/insights')) {
      return matchPath(currentPath, {
        path: '/teacher/insights',
        exact: false
      });
    }

    if (url.startsWith('/teacher/bulk-upload')) {
      return matchPath(currentPath, {
        path: '/teacher/bulk-upload',
        exact: false
      });
    }

    if (
      url.startsWith('/teacher/V2/assignments') ||
      url.startsWith('/teacher/home')
    ) {
      return (
        currentPath.startsWith('/teacher/V2/assignments') ||
        currentPath.startsWith('/teacher/assignments') ||
        currentPath.startsWith('/teacher/home')
      );
    }

    return !isNull(
      matchPath(currentPath, {
        path: url,
        exact: false
      })
    );
  };

  useEffect(() => {
    if (
      shouldExpandAssignmentsList &&
      !isNil(latestAssignmentsPerSection?.results) &&
      !isLoadingLatestAssignments
    ) {
      setResults((prevResults) => [
        ...prevResults,
        ...latestAssignmentsPerSection.results
      ]);
      setShouldExpandAssignmentsList(false);
    } else if (!shouldExpandAssignmentsList && pagination.offset === 0) {
      setResults(latestAssignmentsPerSection?.results);
    }
  }, [
    latestAssignmentsPerSection?.results,
    shouldExpandAssignmentsList,
    isLoadingLatestAssignments,
    pagination.offset
  ]);

  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, clientHeight, scrollHeight } = e.target;

      if (
        scrollTop + clientHeight >= scrollHeight - 5 &&
        !isNil(latestAssignmentsPerSection) &&
        !isNil(latestAssignmentsPerSection.next)
      ) {
        setPagination((prev) => ({
          ...prev,
          offset: latestAssignmentsPerSection.offset + 20
        }));
        setShouldExpandAssignmentsList(true);
      }
    },
    [latestAssignmentsPerSection]
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1); // trigger re-render
    }, 60000); // update every minute

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <Drawer
        open={open}
        variant={lgScreen ? 'permanent' : 'temporary'}
        classes={{
          paper: classnames(styles.drawer, { [styles.small]: !open })
        }}
        onClose={toggle}
      >
        <div className={styles.menuContent}>
          <div className={styles.menuActions}>
            <Link
              className={styles.logo}
              href={logoRedirectUrl}
              onClick={toggleNavigationOnTabletResolution}
            >
              <SideNavItemWrapper open={open} text="Home">
                <img src={open ? logoBig : logoSmall} alt="Logo" />
              </SideNavItemWrapper>
            </Link>

            <List component="div" classes={{ root: styles.menuList }}>
              {map(
                leftNavigationItems,
                ({
                  key,
                  text,
                  shortText,
                  url,
                  Icon,
                  dataIntercomTargetName
                }) => {
                  const selected = isSelected({
                    url,
                    currentPath: history.location.pathname
                  });

                  if (key === 'create') {
                    return (
                      <div
                        key={key}
                        onClick={() => {
                          toggleNavigationOnTabletResolution();
                          setIsAssignmentDialogOpened(true);
                        }}
                        data-intercom-target={
                          dataIntercomTargetName
                            ? dataIntercomTargetName
                            : undefined
                        }
                      >
                        <SideNavItemWrapper open={open} text={text}>
                          <ListItem
                            button
                            selected={isAssignmentDialogOpened}
                            classes={{
                              root: styles.listItem,
                              selected: styles.selected
                            }}
                          >
                            <SvgIcon
                              component={Icon}
                              htmlColor={
                                isAssignmentDialogOpened
                                  ? colors.blue5
                                  : colors.white
                              }
                            />
                            {open && (
                              <Typography
                                classes={{ root: styles.listItemText }}
                                variant="H-TEXT-2"
                                color={
                                  isAssignmentDialogOpened
                                    ? colors.blue5
                                    : colors.white
                                }
                              >
                                {shortText || text}
                              </Typography>
                            )}
                          </ListItem>
                        </SideNavItemWrapper>
                      </div>
                    );
                  }
                  return (
                    <Link
                      key={key}
                      href={url}
                      onClick={toggleNavigationOnTabletResolution}
                      className={styles.listItemLink}
                    >
                      <SideNavItemWrapper open={open} text={text}>
                        <ListItem
                          button
                          selected={selected}
                          classes={{
                            root: styles.listItem,
                            selected: styles.selected
                          }}
                        >
                          <Icon
                            htmlColor={selected ? colors.blue5 : colors.white}
                          />
                          {open && (
                            <Typography
                              classes={{ root: styles.listItemText }}
                              variant="H-TEXT-2"
                              color={selected ? colors.blue5 : colors.white}
                            >
                              {text}
                            </Typography>
                          )}
                        </ListItem>
                      </SideNavItemWrapper>
                    </Link>
                  );
                }
              )}
            </List>
            {open && SIDE_NAVIGATION_ASSIGNMENTS_LIST_ENABLED && (
              <div className={styles.assignmentsList}>
                <Divider />
                <div className={styles.assignmentsListHeader}>
                  <AssignemntsIcon />
                  <Typography
                    color={colors.white}
                    className={styles.headerText}
                  >
                    Assignments
                  </Typography>
                </div>
                <div className={styles.updatedAt}>
                  {lastUpdated &&
                    `Updated ${formatRelativeTime({ date: lastUpdated })}`}
                </div>

                {isLoadingLatestAssignments && isNil(results) && (
                  <div className={styles.assignmentsListContent}>
                    <Skeleton animation="wave" height={80} />
                    <Skeleton animation="wave" height={80} />
                    <Skeleton animation="wave" height={80} />
                  </div>
                )}

                {!isNil(results) && (
                  <Box
                    className={styles.assignmentsListContent}
                    onScroll={handleScroll}
                  >
                    {results.map((item) => {
                      const submittedCount =
                        item.celebrate_count +
                        item.support_count +
                        item.intervene_count +
                        item.needs_review_count +
                        item.loading_count;
                      const celebratePercentage =
                        (item.celebrate_count / submittedCount) * 100;
                      const supportPercentage =
                        (item.support_count / submittedCount) * 100;
                      const intervenePercentage =
                        (item.intervene_count / submittedCount) * 100;
                      const needsReviewPercentage =
                        (item.needs_review_count / submittedCount) * 100;
                      const loadingPercentage =
                        (item.loading_count / submittedCount) * 100;

                      return (
                        <Box
                          key={item.tracker_instance_section_id}
                          className={styles.card}
                          onClick={() => {
                            history.push({
                              pathname: reverse(
                                TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL,
                                {
                                  trackerInstanceSectionId:
                                    item.tracker_instance_section_id
                                }
                              )
                            });
                            trackMixpanelEvent(
                              user.id,
                              'assignment_side_navigation_clicked',
                              {
                                feature: 'side navigation',
                                view: 'assignmnent list',
                                normalizedURL: history.location.pathname
                                  .replace(/\/\d+/g, '/testId')
                                  .replace(/\/$/, ''),
                                class_id: item.section_id,
                                class_name: item.section_name,
                                assignment_id: item.tracker_instance_section_id,
                                assignment_name: item.tracker_name
                              }
                            );
                          }}
                        >
                          <Tooltip title={item.section_name} arrow>
                            <Box className={styles.title}>
                              {item.section_name}
                            </Box>
                          </Tooltip>
                          <Tooltip
                            key={item.tracker_instance_section_id}
                            title={item.tracker_name}
                            arrow
                          >
                            <Box className={styles.subtitle}>
                              {item.submited_percentage}% Submitted
                            </Box>
                          </Tooltip>
                          <div className={styles.progress}>
                            <Tooltip
                              arrow
                              classes={{
                                tooltip: `${colorClassMap.celebrate}`,
                                arrow: `${arrowColorClassMap.celebrate}`
                              }}
                              title={`Celebrate (${item.celebrate_count})`}
                            >
                              <div
                                className={styles.progressBarCelebrate}
                                role="progressbar"
                                style={{ width: `${celebratePercentage}%` }}
                              />
                            </Tooltip>
                            <Tooltip
                              arrow
                              classes={{
                                tooltip: `${colorClassMap.support}`,
                                arrow: `${arrowColorClassMap.support}`
                              }}
                              title={`Support (${item.support_count})`}
                            >
                              <div
                                className={styles.progressBarSupport}
                                role="progressbar"
                                style={{
                                  width: `${supportPercentage}%`,
                                  left: `${celebratePercentage}%`
                                }}
                              />
                            </Tooltip>
                            <Tooltip
                              arrow
                              classes={{
                                tooltip: `${colorClassMap.intervene}`,
                                arrow: `${arrowColorClassMap.intervene}`
                              }}
                              title={`Intervene (${item.intervene_count})`}
                            >
                              <div
                                className={styles.progressBarIntervene}
                                role="progressbar"
                                style={{
                                  width: `${intervenePercentage}%`,
                                  left: `${
                                    celebratePercentage + supportPercentage
                                  }%`
                                }}
                              />
                            </Tooltip>
                            <Tooltip
                              arrow
                              classes={{
                                tooltip: `${colorClassMap.needsReview}`,
                                arrow: `${arrowColorClassMap.needsReview}`
                              }}
                              title={`Needs Review (${item.needs_review_count})`}
                            >
                              <div
                                className={styles.progressBarNeedsReview}
                                role="progressbar"
                                style={{
                                  width: `${needsReviewPercentage}%`,
                                  left: `${
                                    celebratePercentage +
                                    supportPercentage +
                                    intervenePercentage
                                  }%`
                                }}
                              />
                            </Tooltip>
                            <Tooltip
                              arrow
                              classes={{
                                tooltip: `${colorClassMap.loading}`,
                                arrow: `${arrowColorClassMap.loading}`
                              }}
                              title={`Loading (${item.loading_count})`}
                            >
                              <div
                                className={styles.progressBarLoading}
                                role="progressbar"
                                style={{
                                  width: `${loadingPercentage}%`,
                                  left: `${
                                    celebratePercentage +
                                    supportPercentage +
                                    intervenePercentage +
                                    needsReviewPercentage
                                  }%`
                                }}
                              />
                            </Tooltip>
                          </div>
                        </Box>
                      );
                    })}
                    {isLoadingLatestAssignments && (
                      <div className={styles.skeletonWrapper}>
                        <Skeleton animation="wave" height={80} />
                      </div>
                    )}
                  </Box>
                )}
              </div>
            )}
          </div>

          <IconButton onClick={toggle}>
            {open ? (
              <LeftArrowIcon
                htmlColor={colors.white}
                classes={{ root: styles.arrowIcon }}
              />
            ) : (
              <RightArrowIcon
                htmlColor={colors.white}
                classes={{ root: styles.arrowIcon }}
              />
            )}
          </IconButton>
        </div>

        {user.is_in_free_trial && <FreeTrialReminder displayProgress={true} />}

        <a
          href="https://www.edlight.com/terms-of-service"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.upgradeLink}
          onClick={(e) => e.stopPropagation()}
        >
          Terms of Use
        </a>
        <a
          href="https://www.edlight.com/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.upgradeLink}
          onClick={(e) => e.stopPropagation()}
        >
          Privacy Policy
        </a>
      </Drawer>
      {isNoAssignmentsDialogOpened && (
        <Dialog
          open
          onClose={closeNoAssignmentsDialog}
          classes={{ paper: styles.dialogPaper }}
        >
          <Typography variant="H-TEXT-2" color={colors.blue1}>
            No Assignments to Review!
          </Typography>
        </Dialog>
      )}

      {isAssignmentDialogOpened && (
        <Dialog
          open
          alignTop
          onClose={() => setIsAssignmentDialogOpened(false)}
          classes={{ paper: styles.assignmentCreateDialog }}
        >
          <TeacherAssignmentCreate />
        </Dialog>
      )}
    </>
  );
};

export default SideNav;
